import React, { useState, useEffect, useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

// Reflect-a-Day
// Mobile-first, responsive React component (Tailwind CSS assumed available)
// Features:
// - Daily reflection entry with mood (1-5)
// - Peace Points earned per entry (mood-weighted)
// - LocalStorage persistence
// - Consecutive-day streak calculation
// - Minimal, accessible UI optimized for phones
// - Dark theme with subtle pink accents
// - Calendar date picker for easy date selection
// - Mood visualization chart

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const STORAGE_KEY = "reflectaday_entries_v1";

function todayISO(date = new Date()) {
  return date.toISOString().slice(0, 10); // YYYY-MM-DD
}

export default function ReflectADay() {
  const [text, setText] = useState("");
  const [mood, setMood] = useState(4); // 1 (low) - 5 (high)
  const [entries, setEntries] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      return {};
    }
  });

  const [selectedDate, setSelectedDate] = useState(todayISO());

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  }, [entries]);

  // Compute today's entry if exists
  useEffect(() => {
    const e = entries[selectedDate];
    if (e) {
      setText(e.text);
      setMood(e.mood);
    } else {
      setText("");
      setMood(4);
    }
  }, [selectedDate]);

  const saveEntry = () => {
    if (!text.trim()) return; // require something
    const newEntries = { ...entries, [selectedDate]: { text: text.trim(), mood, ts: Date.now() } };
    setEntries(newEntries);
    // small celebratory pulse (handled in UI by class animation)
    // keep local for immediate UI feedback
  };

  const deleteEntry = (date) => {
    const copy = { ...entries };
    delete copy[date];
    setEntries(copy);
  };

  // Peace Points: base 10 + mood*5
  const peacePointsFor = (entry) => 10 + (entry.mood - 1) * 5;

  // Total Peace Points
  const totalPeacePoints = useMemo(() => {
    return Object.values(entries).reduce((s, e) => s + peacePointsFor(e), 0);
  }, [entries]);

  // Streak calculation: count contiguous days up to today
  const streak = useMemo(() => {
    const dates = new Set(Object.keys(entries));
    let count = 0;
    let cursor = new Date();
    while (true) {
      const iso = todayISO(cursor);
      if (dates.has(iso)) {
        count += 1;
        // move back 1 day
        cursor.setDate(cursor.getDate() - 1);
      } else break;
    }
    return count;
  }, [entries]);

  // Quick nav: prev / next date
  const changeDay = (delta) => {
    const d = new Date(selectedDate + "T00:00:00");
    d.setDate(d.getDate() + delta);
    setSelectedDate(todayISO(d));
  };

  // List recent entries (max 7)
  const recent = useMemo(() => {
    return Object.keys(entries)
      .sort((a, b) => (a < b ? 1 : -1))
      .slice(0, 7);
  }, [entries]);

  // Chart data for mood visualization
  const chartData = useMemo(() => {
    if (recent.length === 0) return null;
    const sortedRecent = recent.slice().reverse().slice(-7); // oldest to newest, max 7
    const labels = sortedRecent.map(d => {
      const date = new Date(d);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    });
    const moods = sortedRecent.map(d => entries[d].mood);
    return {
      labels,
      datasets: [{
        label: 'Mood',
        data: moods,
        backgroundColor: moods.map(m => 
          m >= 4 ? 'rgba(244, 114, 182, 0.8)' : // pink
          m === 3 ? 'rgba(251, 191, 36, 0.8)' : // amber
          'rgba(239, 68, 68, 0.8)' // red
        ),
        borderColor: moods.map(m => 
          m >= 4 ? 'rgba(244, 114, 182, 1)' :
          m === 3 ? 'rgba(251, 191, 36, 1)' :
          'rgba(239, 68, 68, 1)'
        ),
        borderWidth: 1,
      }],
    };
  }, [recent, entries]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(17, 24, 39, 0.8)',
        titleColor: '#f3f4f6',
        bodyColor: '#f3f4f6',
        borderColor: 'rgba(244, 114, 182, 0.5)',
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        grid: {
          color: 'rgba(244, 114, 182, 0.1)',
        },
        ticks: {
          color: '#d1d5db',
        },
      },
      y: {
        beginAtZero: true,
        max: 5,
        grid: {
          color: 'rgba(244, 114, 182, 0.1)',
        },
        ticks: {
          stepSize: 1,
          color: '#d1d5db',
        },
      },
    },
  }), []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-gray-950 p-3 sm:p-4 md:p-6">
      <header className="max-w-sm mx-auto sm:max-w-md md:max-w-lg lg:max-w-2xl">
        <h1 className="text-xl sm:text-2xl font-bold leading-tight text-white mb-2">🪞 Reflect-a-Day</h1>
        <p className="text-xs sm:text-sm mb-4 text-yellow-300">Daily mini-journal · build calm, track mood & wins</p>

        <div className="mb-4 space-y-3">
          <div className="flex items-center justify-center">
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="px-3 py-2 rounded-md border border-pink-600 bg-black text-white focus:outline-none focus:ring-2 focus:ring-pink-500 text-sm w-full max-w-xs"
            />
          </div>
          <div className="flex gap-1 sm:gap-2 items-center justify-center">
            <button
              className="px-2 sm:px-3 py-1.5 sm:py-1 rounded-md text-xs sm:text-sm border border-pink-600 shadow-sm text-yellow-300 hover:bg-gray-800 transition-colors"
              onClick={() => changeDay(-1)}
              aria-label="Previous day"
            >
              ◀
            </button>
            <button
              className="px-3 sm:px-3 py-1.5 sm:py-1 rounded-md text-xs sm:text-sm border border-pink-600 shadow-sm text-yellow-300 hover:bg-gray-800 transition-colors"
              onClick={() => setSelectedDate(todayISO())}
              aria-label="Jump to today"
            >
              Today
            </button>
            <button
              className="px-2 sm:px-3 py-1.5 sm:py-1 rounded-md text-xs sm:text-sm border border-pink-600 shadow-sm text-yellow-300 hover:bg-gray-800 transition-colors"
              onClick={() => changeDay(1)}
              aria-label="Next day"
            >
              ▶
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 mb-6">
          <label className="text-xs text-white">How are you feeling? <span className="text-yellow-300">(mood)</span></label>
          <input
            type="range"
            min="1"
            max="5"
            value={mood}
            onChange={(e) => setMood(parseInt(e.target.value, 10))}
            className="w-full accent-pink-500"
          />
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 text-sm">
            <div className="flex items-center gap-2">
              <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-white font-semibold transition-transform text-xs sm:text-sm ${mood >= 4 ? 'bg-pink-400 scale-105' : mood === 3 ? 'bg-pink-500' : 'bg-rose-600'}`}>
                {mood}
              </div>
              <div className="text-xs text-yellow-400">Mood level</div>
            </div>

            <div className="text-right sm:text-base">
              <div className="text-xs text-yellow-500">Peace pts (this entry)</div>
              <div className="font-medium text-white">{10 + (mood - 1) * 5}</div>
            </div>
          </div>

          <label className="text-xs text-white">Write a short reflection (3-200 chars)</label>
          <textarea
            maxLength={1000}
            value={text}
            style={{width: "100%", height: "300px"}}
            onChange={(e) => setText(e.target.value)}
            className="resize-none p-3 rounded-md border border-gray-700 focus:outline-none focus:ring-2 focus:ring-pink-500 bg-black text-white placeholder-gray-500 w-full text-sm"
            placeholder="What happened today? What are you grateful for?"
          />

          <div className="flex flex-row items-center sm:flex-row gap-2">
            <button
              onClick={saveEntry}
              className="px-4 py-3 sm:py-2 rounded-lg font-medium text-white bg-pink-600 shadow-md hover:bg-pink-500 transition-colors"
            >
              Save
            </button>
            <button
              onClick={() => {
                setText("");
                setMood(4);
              }}
              className="px-4 py-3 sm:px-4 sm:py-2 rounded-lg border border-gray-600 text-yellow-300 hover:bg-gray-800 transition-colors"
            >
              Clear
            </button>
          </div>
        </div>

        <section className="bg-black p-3 sm:p-4 rounded-lg shadow-sm border border-pink-800 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-3 sm:mb-4">
            <div className="text-left sm:text-center">
              <div className="text-xs text-yellow-500">Streak</div>
              <div className="text-base sm:text-lg font-semibold text-white">{streak} 🔥</div>
            </div>
            <div className="text-right sm:text-center">
              <div className="text-xs text-yellow-500">Total Peace Points</div>
              <div className="text-base sm:text-lg font-semibold text-white">{totalPeacePoints} ✨</div>
            </div>
          </div>

          {/* Mood Visualization Chart */}
          {chartData && (
            <div className="h-48 sm:h-64 mb-4">
              <div className="text-xs text-yellow-500 mb-2 text-center">Mood over recent days</div>
              <Bar data={chartData} options={chartOptions} />
            </div>
          )}

          <div>
            <div className="text-xs text-yellow-500 mb-2">Recent entries</div>
            <div className="space-y-3 sm:space-y-2">
              {recent.length === 0 && <div className="text-sm text-gray-400">No entries yet — start with a short note today.</div>}
              {recent.map((d) => (
                <div key={d} className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="flex-1">
                    <div className="text-xs text-yellow-400">{d}</div>
                    <div className="text-sm text-white">{entries[d].text.slice(0, 120)}{entries[d].text.length > 120 ? '...' : ''}</div>
                  </div>
                  <div className="text-right sm:text-left">
                    <div className="text-xs text-gray-400">Mood {entries[d].mood}</div>
                    <div className="text-xs text-gray-400">{peacePointsFor(entries[d])} pts</div>
                    <button
                      onClick={() => deleteEntry(d)}
                      className="mt-1 sm:mt-2 text-xs text-pink-400 hover:text-pink-200 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <footer className="mb-4 text-center text-xs text-yellow-500">
          Data saved locally in your browser. Use export/import to backup.
        </footer>

        {/* Small export/import utilities (lightweight) */}
        <div className="flex flex-row items-center gap-2">
          <button
            onClick={() => {
              const blob = new Blob([JSON.stringify(entries)], { type: "application/json" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `reflectaday_${todayISO()}.json`;
              a.click();
              URL.revokeObjectURL(url);
            }}
            className="py-3 sm:py-2 rounded-md border border-gray-600 text-xs sm:text-sm text-yellow-300 hover:bg-gray-800 transition-colors"
          >
            Export
          </button>
          <button className="py-3 sm:py-2 rounded-md border border-gray-600 text-xs sm:text-sm text-center cursor-pointer text-yellow-300 hover:bg-gray-800 transition-colors">
            <input
              type="file"
              accept="application/json"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                const reader = new FileReader();
                reader.onload = (ev) => {
                  try {
                    const parsed = JSON.parse(ev.target.result);
                    setEntries((prev) => ({ ...prev, ...parsed }));
                  } catch (err) {
                    alert("Invalid file");
                  }
                };
                reader.readAsText(f);
              }}
            />
            Import
          </button>
        </div>
      </header>
    </div>
  );
}